import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { CustomerEmailMessage } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import { input, nonEmptyValidator } from "@redotech/ui/form";
import { optionalEmailValidator } from "@redotech/web-util/form";
import classNames from "classnames";
import { FormEvent, memo } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../arbiter-components/input/redo-text-input";
import { Flex } from "../../flex";
import { HandleEmailInputFn, HandleLiveChatHandoffFn } from "../common";
import { ContactSupportMode } from "./contact-support";
import * as css from "./subscribe-to-back-in-stock-tool-message.module.css";

const emailInput = input<string>({
  validator: (value: string) => [
    ...optionalEmailValidator(value),
    ...nonEmptyValidator(value),
  ],
});

export const AiEmailInput = memo(function AiEmailInput({
  message,
  readOnly,
  onEmailEntry,
  conversationId,
  buttonText,
  messageSide = "left",
  mode = "email",
}: {
  message: CustomerEmailMessage;
  readOnly: boolean;
  onEmailEntry?: HandleEmailInputFn | HandleLiveChatHandoffFn | undefined;
  conversationId: string;
  buttonText: string;
  messageSide?: "left" | "right";
  mode?: ContactSupportMode;
}) {
  const input = useInput(
    emailInput,
    message?.structuredContent?.customerEmail ?? "",
  );

  const [entryLoad, doEntryLoad] = useTriggerLoad(async () => {
    if (onEmailEntry) {
      await onEmailEntry({
        conversationId,
        messageId: message._id,
        customerEmail: input.value,
        pendingToolCallId: message.pendingToolCallId ?? undefined,
      });
    }
  });

  const onFormSubmit = useHandler((event?: FormEvent<HTMLElement>) => {
    if (input.allErrors.length > 0) {
      return;
    }

    event?.preventDefault();
    doEntryLoad();
  });

  const disabled = readOnly || entryLoad.pending || !onEmailEntry;

  return (
    <Flex
      align="flex-end"
      as="form"
      className={classNames(css.subscribeToBackInStockBubbleContainer, {
        [css.messageSideLeft]: messageSide === "left",
        [css.messageSideRight]: messageSide === "right",
      })}
      dir="row"
      gap="md"
      onSubmit={onFormSubmit}
      px="lg"
      py="md"
    >
      <Flex dir="column" grow={1}>
        {/* This column was necessary because className on RedoTextInput is applied to a child of the input container */}
        <RedoTextInput
          label="Email"
          placeholder="Enter your email"
          setValue={input.setValue}
          state={disabled ? "disabled" : undefined}
          value={input.value}
        />
      </Flex>
      <RedoButton
        disabled={disabled || input.allErrors.length > 0}
        hierarchy="primary"
        onClick={() => onFormSubmit()}
        text={buttonText}
      />
    </Flex>
  );
});
